import { Alert, Box, Grid, Switch, TableCell, Typography } from "@mui/material";
import StyledTableRow from "../../../components/table/styled-table-row";
import { Member, MemberRequest } from "../../../service/model/member.model";
import { ReactNode, useState } from "react";
import MemberForm from "./member-form";
import { ConfirmationDialog } from "../../../components/confiramation-dialog";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { deleteMember, fetchMembers, updateMember, updateMemberLoginStatus } from "../member.slice";
import AttendanceForm from "../../attendance/components/attendance-form";
import { Attendance, AttendanceRequest } from "../../../service/model/attendance.model";
import { createAttendance } from "../../attendance/attendance.slice";
import { MemberPayment, MemberPaymentRequest } from "../../../service/model/member-payment.model";
import { createMemberPayment } from "../../member-payments/member-payment.slice";
import MemberPaymentForm from "../../member-payments/components/member-payment-form";
import MemberMessageModal from "../../message/components/member/member-message-modal";
import TableActionButton from "../../../components/table/table-action-button";
import * as memberService from "../../../service/member.service";
import NoteModal from "../../notes/components/note-modal";
import { NoteTargetType, PermissionCode } from "../../../shared/enums";
import { S3KeyGenerator } from "../../../helpers/s3-helpers";
import ProfileImage from "./profile-image";
import { selectLoggedInUser } from "../../auth/auth.slice";
import { getMembershipStatusChip, getPermissionsForLoggedInUser } from "../../../shared/utils";
import { selectMemberships } from "../../memberships/membership.slice";
import { MoneyIcon, AttendanceIcon, MessageIcon, ChecklistIcon, EditIcon, DeleteIcon, RestaurantMenuIcon, FitnessCenterIcon } from "../../../shared/icons";
import StyledChip from "../../../components/styled-chip";
import MoreActions from "../../../components/more-actions";
import { useNavigate } from "react-router-dom";

interface TableRowProps {
    readonly member: Member
}

type MembershipStatus = 'Active' | 'Expired';
type AttendanceStatus = 'Active' | 'Inactive';

const MemberTableRow: React.FC<TableRowProps> = ({ member }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const loggedInUser = useAppSelector(selectLoggedInUser);
    const loggedInUserPermissions = getPermissionsForLoggedInUser(loggedInUser);
    const memberships = useAppSelector(selectMemberships);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showMemberForm, setShowMemberForm] = useState(false);
    const [showAttendanceForm, setShowAttendanceForm] = useState(false);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showLoginConfirmationDialog, setShowLoginConfirmationDialog] = useState(false);

    const handleDelete = () => {
        dispatch(deleteMember(member.id))
        setShowDeleteDialog(false)
    }

    const handleDeleteCancel = () => {
        setShowDeleteDialog(false)
    }

    const handleLoginStatusChange = () => {
        dispatch(updateMemberLoginStatus({ memberId: member.id, loginEnabled: !member.loginEnabled }))
        setShowLoginConfirmationDialog(false)
    }

    const handleLoginStatusChangeCancel = () => {
        setShowLoginConfirmationDialog(false)
    }

    const handleSubmit = async (data: MemberRequest, image?: File | null) => {
        dispatch(updateMember({ ...data, id: member.id })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                setShowMemberForm(false);
            }
        });

        if (image) {
            const extension = image.name.split('.').pop();
            if (extension) {
                const key = S3KeyGenerator.memberProfileImageKey(data.businessId, member.id, extension);
                const presignedUrl = await memberService.getPresignedUrl(member.id, extension);
                await memberService.uploadProfileImageToS3(presignedUrl, image);
                await memberService.updateMemberImageReference(member.id, key);
                dispatch(fetchMembers());
            }
        }
        if (member.profileImageKey && !image) {
            await memberService.updateMemberImageReference(member.id, null);
            dispatch(fetchMembers());
        }
    };

    const handleAttendanceSubmit = (data: AttendanceRequest) => {
        dispatch(createAttendance({ ...data, markedByType: 'STAFF', markedByUserId: loggedInUser?.user.id })).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                setShowAttendanceForm(false);
            }
        });
    };

    const handlePaymentSubmit = (data: MemberPaymentRequest) => {
        dispatch(createMemberPayment(data)).then(resp => {
            if (resp.meta.requestStatus === 'fulfilled') {
                setShowPaymentForm(false);
            }
        });
    };

    const getAttendanceStatusChip = (status: AttendanceStatus): ReactNode => {
        switch (status) {
            case "Active": {
                return <StyledChip label={status} color="success" />
            }
            case "Inactive": {
                return <StyledChip label={status} color="warning" />
            }
            default: {
                return <></>
            }
        }
    }

    const getMemberLoginConfirmationMessage = (loginEnabled: boolean): ReactNode => {
        return <>
            <Alert severity="warning">It is recommended to update the member login status along with a membership payment.</Alert>
            <Box sx={{ mt: 2 }}>
                <Typography component="span">Are you sure you want to <strong>{loginEnabled ? 'disable' : 'enable'}</strong> login status for {member.firstName} {member.lastName} ?</Typography>
            </Box>
        </>
    }

    // const getLoginStatusIcon = (loginEnabled: boolean): ReactNode => {
    //     if (loginEnabled) {
    //         return <VerifiedIcon color="success" />
    //     }
    //     return <NotInterestedIcon color="error" />
    // }

    return (
        <>
            <StyledTableRow key={member.id}>
                <TableCell>
                    <Box component="div" display="inline" className="responsive-mobile-heading">Name</Box>
                    <div style={{ display: "inline-flex", alignItems: "center" }}>
                        <ProfileImage profileImageKey={member.profileImageKey} />
                        <span style={{ fontWeight: "bold", marginLeft: "4px" }}>{member.firstName} {member.lastName}</span>
                    </div>
                </TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Contact</Box>{member.contact}</TableCell>
                {/* <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Gender</Box>{member.gender}</TableCell> */}
                {/* <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Due</Box>{member.dueAmount}</TableCell> */}
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Membership</Box>{memberships.find(membership => membership.id === member.membershipId)?.name}</TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Expiry Date</Box>{member.expiryDate}</TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Last Attended Date</Box>{member.lastAttendedDate}</TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Attendance Status</Box>{getAttendanceStatusChip(member.attendanceStatus as AttendanceStatus)}</TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Membership Status</Box>{getMembershipStatusChip(member.membershipStatus as MembershipStatus)}</TableCell>
                <TableCell><Box component="div" display="inline" className="responsive-mobile-heading">Login</Box>
                    <Switch
                        checked={member.loginEnabled}
                        disabled={!loggedInUserPermissions.includes(PermissionCode.MEMBER_LOGIN_MANAGE)}
                        color="primary"
                        onChange={(e) => setShowLoginConfirmationDialog(true)} />
                </TableCell>
                <TableCell>
                    <Box component="div" display="inline" className="responsive-mobile-heading">Actions</Box>
                    <Grid display="flex">
                        {/* <IconButton onClick={() => setShowMemberForm(true)} sx={{color: `${theme.palette.secondary.main}`, paddingLeft: 0}}><VisibilityIcon /></IconButton> */}
                        {loggedInUserPermissions.includes(PermissionCode.MEMBER_PAYMENTS_CREATE) &&
                            <TableActionButton onClick={() => setShowPaymentForm(true)} label="Add Payment" icon={<MoneyIcon />} />
                        }
                        {loggedInUserPermissions.includes(PermissionCode.MEMBER_ATTENDANCE_MARK) &&
                            <TableActionButton onClick={() => setShowAttendanceForm(true)} label="Mark Attendance" icon={<AttendanceIcon />} />
                        }
                        {loggedInUserPermissions.includes(PermissionCode.NUTRITION_PLANS_VIEW) &&
                            <TableActionButton onClick={() => navigate(`/members/${member.id}/nutrition-plan`)} label="Nutrition Plan" icon={<RestaurantMenuIcon />} />
                        }
                        {loggedInUserPermissions.includes(PermissionCode.WORKOUT_PLANS_VIEW) &&
                            <TableActionButton onClick={() => navigate(`/members/${member.id}/workout-plan`)} label="Workout Plan" icon={<FitnessCenterIcon />} />
                        }
                        {/* <TableActionButton onClick={() => setShowMessageModal(true)} label="Send Message" icon={<MessageIcon />} />
                        <TableActionButton onClick={() => setShowNoteModal(true)} label="Notes" icon={<ChecklistIcon />} /> */}
                        {/* <TableActionButton onClick={() => setShowMemberForm(true)} label="Edit" icon={<EditIcon />} />
                        <TableActionButton onClick={() => setShowDeleteDialog(true)} label="Delete" icon={<DeleteIcon />} /> */}
                        <MoreActions options={[
                            { label: 'Send Message', icon: <MessageIcon />, hidden: !loggedInUserPermissions.includes(PermissionCode.MESSAGES_SEND), onClick: () => setShowMessageModal(true) },
                            { label: 'Notes', icon: <ChecklistIcon />, hidden: !loggedInUserPermissions.includes(PermissionCode.NOTES_VIEW), onClick: () => setShowNoteModal(true) },
                            { label: 'Edit', icon: <EditIcon />, hidden: !loggedInUserPermissions.includes(PermissionCode.MEMBERS_EDIT), onClick: () => setShowMemberForm(true) },
                            { label: 'Delete', icon: <DeleteIcon />, hidden: !loggedInUserPermissions.includes(PermissionCode.MEMBERS_DELETE), onClick: () => setShowDeleteDialog(true) },
                        ]} />
                    </Grid>
                </TableCell>
            </StyledTableRow>
            {showPaymentForm &&
                <MemberPaymentForm payment={{ memberId: member.id, firstName: member.firstName, lastName: member.lastName } as MemberPayment} editable={true} onClose={() => setShowPaymentForm(false)} onSubmit={handlePaymentSubmit} />
            }
            {showAttendanceForm &&
                <AttendanceForm attendance={{ memberId: member.id, memberName: `${member.firstName} ${member.lastName}` } as Attendance} editable={true} onClose={() => setShowAttendanceForm(false)} onSubmit={handleAttendanceSubmit} />
            }
            {showMessageModal &&
                <MemberMessageModal member={member} onClose={() => setShowMessageModal(false)} />
            }
            {showNoteModal &&
                <NoteModal target={member} targetType={NoteTargetType.MEMBER} onClose={() => setShowNoteModal(false)} />
            }
            {showMemberForm &&
                <MemberForm member={member} editable={true} onClose={() => setShowMemberForm(false)} onSubmit={handleSubmit} />
            }
            {showDeleteDialog &&
                <ConfirmationDialog show={true} message={`Are you sure you want to delete ${member.firstName} ?`} onConfirm={handleDelete} onCancel={handleDeleteCancel} />
            }
            {showLoginConfirmationDialog &&
                <ConfirmationDialog show={true} message={getMemberLoginConfirmationMessage(member.loginEnabled)} onConfirm={handleLoginStatusChange} onCancel={handleLoginStatusChangeCancel} />
            }
        </>
    );
}

export default MemberTableRow;